import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Link, Typography, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { providerApi } from '../services/api';

// ── Subscription return ──────────────────────────────────────────────────────
//
// Spec §4.3 confirm: the server fetches the live PayPal snapshot and activates. 202 means
// PayPal has not flipped the subscription to ACTIVE yet, so confirm is re-called (the
// subscription row may not exist yet, so GET cannot be the poll target).

function SubscriptionReturn({ cancelled }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('pending'); // pending | active | unavailable
  const subscriptionId = searchParams.get('subscription_id');

  useEffect(() => {
    if (cancelled || !subscriptionId) return undefined;
    let attempts = 0;
    let timer;
    let stopped = false;
    const tryConfirm = async () => {
      attempts += 1;
      try {
        const res = await providerApi.confirmProSubscription(subscriptionId);
        if (stopped) return;
        if (res.status === 200) {
          setState('active');
          // Full page reload so AuthContext re-fetches providerData (isProPlan=true) on arrival.
          timer = setTimeout(() => { window.location.replace('/dashboard?tab=upgrade'); }, 1500);
          return;
        }
        if (attempts <= 5) timer = setTimeout(tryConfirm, 3000);
      } catch (e) {
        if (stopped) return;
        setState(e?.response?.status === 503 ? 'unavailable' : 'pending');
        if (attempts <= 5) timer = setTimeout(tryConfirm, 3000);
      }
    };
    tryConfirm();
    return () => { stopped = true; clearTimeout(timer); };
  }, [cancelled, subscriptionId]);

  if (cancelled) {
    return (
      <>
        <Alert severity="warning" sx={{ maxWidth: 400 }}>{t('paypal.subscriptionCancelled')}</Alert>
        <Link component={RouterLink} to="/dashboard?tab=upgrade" sx={{ mt: 1 }}>{t('paypal.backToUpgrade')}</Link>
      </>
    );
  }
  if (state === 'active') {
    return (
      <>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
        <Typography variant="h6">{t('paypal.subscriptionActive')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('paypal.redirecting')}</Typography>
      </>
    );
  }
  return (
    <>
      <CircularProgress />
      <Typography variant="h6">{t('paypal.subscriptionPending')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
        {state === 'unavailable' ? t('paypal.subscriptionUnavailable') : t('paypal.subscriptionPendingBody')}
      </Typography>
      <Link component={RouterLink} to="/dashboard?tab=upgrade" sx={{ mt: 1 }}>{t('paypal.backToUpgrade')}</Link>
    </>
  );
}

// ── Payout onboarding return (existing behaviour, unchanged) ─────────────────

function OnboardingReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('loading'); // loading | success | pending | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const merchantIdInPayPal = searchParams.get('merchantIdInPayPal') || searchParams.get('merchantId');

    if (!merchantIdInPayPal) {
      setState('error');
      setErrorMessage(t('paypal.noMerchantId'));
      return;
    }

    // Server-side verification: the backend checks PayPal's tracking_id before persisting
    // anything, and reports PENDING until payments are receivable — only COMPLETED is
    // "connected"; PENDING gets its own state with a recheck action.
    providerApi.verifyPaypalOnboarding(merchantIdInPayPal)
      .then((data) => {
        if (data?.status === 'COMPLETED') {
          setState('success');
          // Full page reload so AuthContext re-fetches providerData (paypalOnboardingStatus=COMPLETED)
          // which causes PayoutOnboardingBanner to disappear on arrival.
          setTimeout(() => { window.location.replace('/dashboard?tab=payments'); }, 1500);
        } else {
          setState('pending');
        }
      })
      .catch((e) => {
        console.error('Failed to complete PayPal onboarding', e);
        setState('error');
        setErrorMessage(t('paypal.verifyFailed'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  return (
    <>
      {state === 'loading' && (
        <>
          <CircularProgress />
          <Typography variant="h6">{t('paypal.completing')}</Typography>
        </>
      )}

      {state === 'success' && (
        <>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h6">{t('paypal.connected')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('paypal.redirecting')}
          </Typography>
        </>
      )}

      {state === 'pending' && (
        <>
          <CircularProgress />
          <Typography variant="h6">{t('paypal.pendingTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
            {t('paypal.pendingBody')}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setState('loading');
              providerApi.verifyPaypalOnboarding()
                .then((data) => {
                  if (data?.status === 'COMPLETED') {
                    setState('success');
                    setTimeout(() => { window.location.replace('/dashboard?tab=payments'); }, 1500);
                  } else {
                    setState('pending');
                  }
                })
                .catch(() => setState('pending'));
            }}
          >
            {t('upgrade.paypalRecheck')}
          </Button>
        </>
      )}

      {state === 'error' && (
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {errorMessage}
        </Alert>
      )}
    </>
  );
}

// ── Top-up return ─────────────────────────────────────────────────────────────
//
// Redirect to wherever CreditTopUpCard is embedded so it can run the capture.
// The card reads ?topup_return=1 and the pending ID from sessionStorage.

function TopUpReturn() {
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to the Promotions tab where CreditTopUpCard lives.
    navigate('/dashboard?tab=promotions&topup_return=1', { replace: true });
  }, [navigate]);

  return null;
}

// ── Root component — branches by ?type param ──────────────────────────────────

const PaypalCallback = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const cancelled = searchParams.get('cancelled') === 'true';

  const inner =
    type === 'subscription' ? (
      <SubscriptionReturn cancelled={cancelled} />
    ) : type === 'topup' ? (
      <TopUpReturn />
    ) : (
      // Default: payout onboarding (backwards compat — no type param).
      <OnboardingReturn />
    );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 4,
      }}
    >
      {inner}
    </Box>
  );
};

export default PaypalCallback;
